import { nanoid } from "@reduxjs/toolkit";
import {
  getEndTime,
  getStartTime,
  isoFormat,
} from "../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../../common/types/autoComplete";

import { format } from "date-fns";
import { OrderRowTemplateSalaryLimit } from "../../../../common/types/apiTypes";
import { parseToInterval } from "../../OrderTimeField";
import {
  AutomatchingStatus,
  FormattedCustomerCompany,
  LongOrderRow,
  OrderFields,
  OrderPreparedPayload,
  OrderPreparedPayloadRow,
  OrderRow,
  OrderTemplate,
  shortOrderDefaultFieldValues,
  ValidationError,
} from "../../orderTypes";
import { timeStringToMinutes, validateFlexibleStartTime } from "../../utils";

type OrderRows = Array<OrderRow>;

type OrderEntryData = {
  customerCompany: FormattedCustomerCompany;
  id: string;
  title: string;
  orderRows: OrderRow[];
};

type DateRange = {
  startTime: Date;
  endTime: Date;
  bundleId: string;
};

const pickSalaryLimit = (
  limits: OrderRowTemplateSalaryLimit[],
  date: Date | null,
) => {
  if (!date) return null;

  const dateString = format(date, "yyyy-MM-dd");
  // limits should be sorted already - at least currently these are in ascending date order
  const limit = [...limits].reverse().find((limit) => limit.date <= dateString);
  return limit ?? null;
};

export const determineSalaryLimit = (
  orderTemplate: OrderTemplate,
  startDate: Date | null,
) => {
  const salaryLimitEntry = pickSalaryLimit(
    orderTemplate.salaryLimits,
    startDate,
  );

  return salaryLimitEntry?.value || null;
};

export const formatData = (data: OrderEntryData): OrderPreparedPayload => {
  const { customerCompany, orderRows, title } = data;
  const { orgId } = customerCompany;

  const formattedRows: OrderPreparedPayloadRow[] = orderRows.flatMap(
    (orderRow) => {
      const { bundleId, rows } = orderRow;

      return rows.map(
        ({
          time,
          date,
          id,
          workLocation,
          customerCostCenter,
          orderTemplate,
          createdAt,
          automatching,
          flexibleStartUntil,
          notifyMinutesBeforeIfNotFulfilled,
          notifyWhenFulfilled,
          ...rest
        }) => {
          const tesVariation = orderTemplate.tesVariation;
          const workLocationId = workLocation.id;
          const customerCostCenterId = customerCostCenter.id;
          const tesId = orderTemplate.tes.id;
          const supplements = orderTemplate.supplements;
          const supplementPriceOverrides =
            orderTemplate.supplementPriceOverrides;
          //TODO the OrdeRow does not need the info type. It should only
          //have the descripton fields
          const customerOrgId = orgId;
          const description =
            (rest?.description ||
              workLocation?.info[orderTemplate.workTitleId]?.fi) ??
            ""; //Might not be defined in the BE config
          const descriptionEn =
            (rest?.descriptionEn ||
              workLocation?.info[orderTemplate.workTitleId]?.en) ??
            ""; //Might not be defined in the BE config
          const descriptionForTalent = workLocation.descriptionForTalent;
          const descriptionForTalentEn = workLocation.descriptionForTalentEng;
          const skills = orderTemplate.skills.map((item) => item.id);
          const trainings = orderTemplate.trainings.map((item) => item.id);
          const certificates = orderTemplate.certificates.map(
            (item) => item.id,
          );
          const orderRowTemplateId = orderTemplate.id;
          const languages = orderTemplate.languages.map((item) => item);
          const periodStartDate =
            orderTemplate.periodStartDate != null
              ? format(orderTemplate.periodStartDate!!, isoFormat)
              : null;
          const startDate = new Date(date as Date);

          const salaryLimit = orderTemplate.salaryLimit;
          const { startDateTime, endDateTime, isNextDay } = parseToInterval(
            time,
            startDate,
          );

          const start = startDateTime.valueOf();
          const end = endDateTime.valueOf();

          let startRange = [start];
          if (flexibleStartUntil) {
            // Handle flexible start
            const flexibleStartDateTime = createDateWithTime(
              startDate,
              flexibleStartUntil,
              isNextDay,
              time.slice(0, 4), // shift start time
            );
            const flexibleStart = flexibleStartDateTime.valueOf();
            startRange = [start, flexibleStart];
          }

          const timeObject = flexibleStartUntil
            ? {
                end,
                startRange: { start: startRange[0], end: startRange[1] },
              }
            : {
                start,
                end,
              };
          const isFillShift = rest?.isFillShift || false;
          const talentIdsToOfferManually = automatching.talents.map(
            (talent) => talent.id,
          );
          const automatchingStartDelayMinutes =
            automatching.automatchingStartDelayMinutes ?? null;
          const automatchingStatus =
            automatchingStartDelayMinutes !== null &&
            automatching.automatchingStatus !== AutomatchingStatus.Off
              ? AutomatchingStatus.Paused
              : automatching.automatchingStatus;

          const talentsFromWorkLocationPool =
            automatching.talentsFromWorkLocationPool || false;

          const teamNotificationSettings = {
            ...(notifyWhenFulfilled && { notifyWhenFulfilled }),
            ...(notifyMinutesBeforeIfNotFulfilled && {
              notifyMinutesBeforeIfNotFulfilled,
            }),
          };

          const isBillable = orderTemplate.isBillable;

          return {
            ...rest,
            bundleId: bundleId ?? nanoid(), // every row has bundleId, some has only one row in the bundle
            customerOrgId,
            time: timeObject,
            workTitleId: orderTemplate.workTitleId,
            tesId,
            skills,
            trainings,
            certificates,
            languages,
            workLocationId,
            customerCostCenterId,
            tesVariation,
            descriptionForTalent,
            descriptionForTalentEn,
            description,
            descriptionEn,
            salaryLimit,
            isFillShift,
            talentIdsToOfferManually,
            automatchingStatus,
            automatchingStartDelayMinutes,
            supplements,
            orderRowTemplateId,
            periodStartDate,
            supplementPriceOverrides,
            talentsFromWorkLocationPool,
            ...(!isEmpty(teamNotificationSettings) && {
              teamNotificationSettings,
            }),
            isBillable,
          };
        },
      );
    },
  );

  return {
    title,
    orderRows: formattedRows,
  };
};

const getRow = (rows: OrderFields) => ({
  ...rows,
  createdAt: Date.now(),
  id: nanoid(),
});

export const findRow = (array: OrderRows, rowId: string) =>
  array.find((item) => item.rowId === rowId);

export const duplicateBundledRow = (
  orderRows: OrderRows,
  rowId: string,
  id: string,
) => {
  const existingBundleContainer = findRow(orderRows, rowId) as OrderRow;

  const existingBundledRowIndex =
    existingBundleContainer &&
    existingBundleContainer.rows.findIndex((row) => row.id === id);

  const duplicatedRow = getRow(
    existingBundleContainer.rows[existingBundledRowIndex],
  );

  const reorderedRows = [
    ...existingBundleContainer.rows.slice(0, existingBundledRowIndex),
    duplicatedRow,
    ...existingBundleContainer.rows.slice(existingBundledRowIndex),
  ];

  const newExistingBundleContainer = {
    ...existingBundleContainer,
    rows: reorderedRows,
  };

  const updatedRows = orderRows.map((row) =>
    row.rowId === existingBundleContainer.rowId
      ? newExistingBundleContainer
      : row,
  );

  return updatedRows;
};

export const deleteBundleRow = (orderRows: OrderRows, bundleRowId: string) =>
  orderRows.filter((row) => row.bundleId !== bundleRowId);

export const deleteBundledSingleRow = (
  rowId: string,
  id: string,
  orderRows: OrderRows,
) =>
  orderRows.map((row) =>
    row.rowId === rowId
      ? { ...row, rows: row.rows.filter((row) => row.id !== id) }
      : row,
  );

const isShortOrderArray = (
  arr: OrderRow[] | LongOrderRow[],
): arr is OrderRow[] => arr[0] && "rows" in arr[0];

//Note that the first function signature assists the second
//signature to type narrow the return type
export function deleteSingleRow<T extends LongOrderRow | OrderRow>(
  rowId: string,
  orderRows: T[],
): T extends OrderRow ? OrderRow[] : LongOrderRow[];
export function deleteSingleRow(
  rowId: string,
  orderRows: OrderRow[] | LongOrderRow[],
): (OrderRow | LongOrderRow)[] {
  if (isShortOrderArray(orderRows)) {
    return orderRows.filter((row) => row.rowId !== rowId);
  } else {
    return orderRows.filter((row) => row.rowId !== rowId);
  }
}

export const duplicateRow = (
  orderRows: OrderRows,
  rowId: string,
  handleError?: (error: boolean, validationError: ValidationError) => void,
) => {
  const existingRowIndex = orderRows.findIndex((row) => row.rowId === rowId);

  const createdAt = Date.now();

  const duplicatedRow = {
    ...orderRows[existingRowIndex],
    rowId: nanoid(),
    createdAt,
    rows: [
      {
        ...orderRows[existingRowIndex].rows[0],
        id: nanoid(),
        createdAt,
      },
    ],
  } as OrderRow;

  if (handleError) {
    validateCopiedRow(duplicatedRow.rows[0], handleError);
  }

  const reorderedRows = [
    ...orderRows.slice(0, existingRowIndex),
    duplicatedRow,
    ...orderRows.slice(existingRowIndex),
  ];

  return reorderedRows;
};

export const removeFromBundle = (
  bundleContainerRow: OrderRow,
  rowItemId: string,
) => {
  const existingBundledRow = bundleContainerRow.rows.find(
    (row) => row.id === rowItemId,
  );
  const newBundledRows = bundleContainerRow.rows.filter(
    (row) => row.id !== existingBundledRow?.id,
  );

  return {
    ...bundleContainerRow,
    rows: newBundledRows,
  };
};

export const addToBundle = (
  bundleContainerRow: OrderRow,
  existingRow: OrderRow,
) => {
  return {
    ...bundleContainerRow,
    rows: [...bundleContainerRow.rows, existingRow.rows[0]],
  };
};

export const transformToSingleRow = (existingBundledRow: OrderFields) => {
  return {
    rowId: nanoid(),
    createdAt: existingBundledRow?.createdAt,
    rows: [existingBundledRow],
  };
};

export const createNewRowToBundle = (sourceRow: OrderFields) => ({
  ...sourceRow,
  id: nanoid(),
  createdAt: Date.now(),
  date: null,
  time: "",
  customerCostCenter: { id: "", name: "" },
});

export const createNewSingleRow = () => {
  const createdAt = Date.now();

  return {
    rowId: nanoid(),
    createdAt,
    duration: "12.01.2023-21.12.2023",
    selected: false,
    rows: [{ ...shortOrderDefaultFieldValues, id: nanoid(), createdAt }],
  };
};

export const createNewBundleContainer = () => {
  const bundleId = nanoid();

  return {
    rowId: nanoid(),
    bundleId,
    createdAt: Date.now(),
    rows: [],
  };
};

export const handleLocalStorage = (
  customerCompany: FormattedCustomerCompany,
) => {
  const fromLocalStorage: FormattedCustomerCompany[] = JSON.parse(
    localStorage.getItem("searchedCompanies") || "",
  );

  const existing = fromLocalStorage.find(
    (item) => item.company === customerCompany.company,
  );

  if (!existing) {
    const updatedLocalStorage = [...fromLocalStorage, customerCompany];

    localStorage.setItem(
      "searchedCompanies",
      JSON.stringify(updatedLocalStorage),
    );
  }
};

export const isFieldValueEmpty = (
  value: string | number | AutocompleteType | Date | null | boolean,
) => {
  if ((value as AutocompleteType)?.id && (value as AutocompleteType)?.name) {
    return isEmpty((value as AutocompleteType).id);
  }

  return isEmpty(value);
};

export type ExtendedOrderFields = OrderFields & { bundleId: string };

const createDateWithTime = (
  date: Date,
  time: string,
  isOvernightShift: boolean = false,
  shiftStartTime?: string,
) => {
  const dateTime = new Date(date);
  const hours = +time.slice(0, 2);
  const minutes = +time.slice(2, 4);

  dateTime.setHours(hours);
  dateTime.setMinutes(minutes);
  dateTime.setSeconds(0);
  dateTime.setMilliseconds(0);

  // Handle overnight shifts for flexible start time
  if (isOvernightShift && shiftStartTime) {
    const shiftStartMinutes = timeStringToMinutes(shiftStartTime);
    const flexibleMinutes = timeStringToMinutes(time);

    // If flexible start time is before shift start time, it's next day
    if (flexibleMinutes < shiftStartMinutes) {
      dateTime.setDate(dateTime.getDate() + 1);
    }
  }

  return dateTime;
};

const isTimeMoreOrEqual = (startTime: Date, endTime: Date): Boolean => {
  return startTime.getTime() > endTime.getTime();
};

const adjustMidnightChange = (rangeA: DateRange, rangeB: DateRange) => {
  if (isTimeMoreOrEqual(rangeA.startTime, rangeA.endTime)) {
    rangeA.endTime.setDate(rangeA.endTime.getDate() + 1);
  }

  if (isTimeMoreOrEqual(rangeB.startTime, rangeB.endTime)) {
    rangeB.endTime.setDate(rangeB.endTime.getDate() + 1);
  }
};

function checkOverlap(
  dateRanges: { startTime: Date; endTime: Date; bundleId: string }[],
) {
  const collidingPairs = [];

  // Loop through the array of date ranges and compare them
  for (let i = 0; i < dateRanges.length - 1; i++) {
    for (let j = i + 1; j < dateRanges.length; j++) {
      const rangeA = dateRanges[i];
      const rangeB = dateRanges[j];

      if (rangeA.bundleId !== rangeB.bundleId) continue;

      // Adjust end dates in case of midnight overlap
      adjustMidnightChange(rangeA, rangeB);

      if (
        isTimeMoreOrEqual(rangeA.endTime, rangeB.startTime) &&
        isTimeMoreOrEqual(rangeB.endTime, rangeA.startTime)
      ) {
        // The date ranges overlap, so add the index pair to the colliding pairs list
        collidingPairs.push([i, j]);
      }
    }
  }

  // Return the list of colliding index pairs
  return collidingPairs;
}

const findRows = (rows: ExtendedOrderFields[], indexes: Array<Number>[]) => {
  const rowIndexes = indexes.flatMap((index) => index);
  const overlappingRows = rows.filter((_, index) => rowIndexes.includes(index));
  return overlappingRows;
};

const formatOverlappingShifts = (rows: ExtendedOrderFields[]) =>
  rows.map((row) => ({
    id: row.id,
    field: "time",
    errorType: "overlappingTimes",
  }));

export const findOverlappingOrders = (rows: ExtendedOrderFields[]) => {
  const datesArray: Array<{
    startTime: Date;
    endTime: Date;
    bundleId: string;
  }> = [];
  let startDateTime: null | Date = null;
  let endDateTime: null | Date = null;

  rows.forEach((row) => {
    if (row.date && row.time) {
      const startTimeString = getStartTime(row.time);
      const endTimeString = getEndTime(row.time);
      startDateTime = createDateWithTime(row.date as Date, startTimeString);
      endDateTime = createDateWithTime(row.date as Date, endTimeString);

      datesArray.push({
        startTime: startDateTime as Date,
        endTime: endDateTime as Date,
        bundleId: row.bundleId,
      });
    }
  });
  const overlappingShiftIndexes = checkOverlap(datesArray);
  const overlappingShifts = findRows(rows, overlappingShiftIndexes);
  const overlappingShiftstoErrorFormat =
    formatOverlappingShifts(overlappingShifts);
  return overlappingShiftstoErrorFormat;
};

export const hasManuallyOfferedRows = (orderRows: OrderRow[]) =>
  orderRows.some((orderRow) =>
    orderRow.rows.some((row) => !!row.automatching.talents.length),
  );

export const getBundleRowValues = (orderRows: OrderRow[], bundleId: string) => {
  const bundleRow = orderRows.find((row) => row.bundleId === bundleId);

  return bundleRow?.rows;
};

const validateCopiedRow = (
  fields: OrderFields,
  handleError: (error: boolean, validationError: ValidationError) => void,
) => {
  if (fields.flexibleStartUntil) {
    const validation = validateFlexibleStartTime(
      fields.flexibleStartUntil,
      fields.time,
      true,
    );

    if (!validation.isValid) {
      handleError(true, {
        id: fields.id,
        field: "flexibleStartUntil",
        errorType: validation.errorType || "invalidFlexibleStart",
      });
    }
  }

  // Add rest of the fields validation here if needed
  // if (fields.time) {
  //   // time validation logic
  // }
};
